import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, EyeOff, Play, Trash2, Upload } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Badge, Button } from '../../components/ui';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import adminMicroSimService from '../../services/adminMicroSimService';
import microSimService from '../../services/microSimService';
import { showError, showSuccess } from '../../utils/toast';

/**
 * Super-admin: one topic — its versions (draft / published / archived) and preview.
 *
 * Publishing a version archives the one before it. Runs keep the content they played,
 * so neither publishing nor unpublishing changes a past report. Only drafts can be
 * deleted; published history is kept.
 */

const STATUS = {
  draft: { label: 'Draft', variant: 'warning' },
  published: { label: 'Published', variant: 'success' },
  archived: { label: 'Archived', variant: 'default' },
};
const formatDate = (d) => (d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const AdminMicroSimTopicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [busy, setBusy] = useState('');
  /** The action waiting on the confirm dialog: { title, message, confirmText, variant, run }. */
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminMicroSimService.getTopic(slug);
      setTopic(res.topic);
    } catch (err) {
      showError(err.message || 'Could not load the topic');
      navigate('/admin/micro-sims');
    }
  }, [slug, navigate]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, success) => {
    setBusy(key);
    try {
      await fn();
      if (success) showSuccess(success);
      await load();
    } catch (err) {
      showError(err.message || 'That did not work');
    } finally {
      setBusy('');
    }
  };

  const preview = (v) => act(`preview-${v.id}`, async () => {
    const res = await microSimService.startPreview(slug, { versionId: v.id });
    navigate(`/admin/micro-sims/${slug}/preview/${res.runId}`);
  });

  const publish = (v) => setPending({
    title: `Publish version ${v.version}?`,
    message: `It replaces the current version of ${topic.title} in every trainer's library. Sessions already played keep their results.`,
    confirmText: 'Publish',
    variant: 'info',
    run: () => act(`publish-${v.id}`, () => adminMicroSimService.publish(v.id), `v${v.version} is live`),
  });

  const unpublish = () => setPending({
    title: `Unpublish ${topic.title}?`,
    message: 'It disappears from every library. Sessions already running finish normally, and past results stay available.',
    confirmText: 'Unpublish',
    variant: 'warning',
    run: () => act('unpublish', () => adminMicroSimService.unpublish(slug), 'Unpublished'),
  });

  const remove = (v) => setPending({
    title: `Delete draft version ${v.version}?`,
    message: 'This draft is removed permanently. Published versions are not affected.',
    confirmText: 'Delete draft',
    variant: 'danger',
    run: () => act(`delete-${v.id}`, () => adminMicroSimService.deleteDraft(v.id), 'Draft deleted'),
  });

  const download = async (v, format) => {
    try {
      await adminMicroSimService.downloadVersion(v.id, format);
    } catch (err) {
      showError(err.message || 'Download failed');
    }
  };

  if (!topic) {
    return (
      <DashboardLayout title="Micro simulation">
        <div className="mx-auto max-w-5xl px-4 py-6"><div className={`${STAT_CARD_BASE} h-48 animate-pulse`} /></div>
      </DashboardLayout>
    );
  }

  const published = topic.versions.find((v) => v.status === 'published');

  return (
    <DashboardLayout title="Micro simulation">
      <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 lg:px-6">
        <button type="button" onClick={() => navigate('/admin/micro-sims')} className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800">
          <ArrowLeft size={15} /> Catalogue
        </button>

        <div className={`${STAT_CARD_BASE} flex flex-wrap items-start justify-between gap-3 p-5`}>
          <div>
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{topic.title}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">
              {topic.slug} · {published ? `v${published.version} is live` : 'not published'} · {topic.launches} launches
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate('/admin/micro-sims')}>
              <Upload size={14} className="mr-1.5" /> Upload a new version
            </Button>
            {published && (
              <Button variant="danger" size="sm" onClick={unpublish} loading={busy === 'unpublish'}>
                <EyeOff size={14} className="mr-1.5" /> Unpublish
              </Button>
            )}
          </div>
        </div>

        <div className={`${STAT_CARD_BASE} overflow-hidden`}>
          <h3 className="px-5 pt-5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Versions</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 dark:bg-neutral-800/50">
                <tr>
                  <th className="px-5 py-2 font-semibold">Version</th>
                  <th className="px-3 py-2 font-semibold">What changed</th>
                  <th className="px-3 py-2 font-semibold">Content</th>
                  <th className="px-3 py-2 font-semibold">Created</th>
                  <th className="px-5 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {topic.versions.map((v) => {
                  const status = STATUS[v.status] || STATUS.archived;
                  return (
                    <tr key={v.id}>
                      <td className="px-5 py-3">
                        <span className="font-semibold">v{v.version}</span>{' '}
                        <Badge variant={status.variant} size="sm">{status.label}</Badge>
                        <span className="block text-[11px] text-neutral-400">from {v.source}</span>
                      </td>
                      <td className="max-w-[220px] px-3 py-3 text-xs text-neutral-600">{v.changeNote || '—'}</td>
                      <td className="px-3 py-3 text-xs text-neutral-600">
                        {v.counts.rounds} rounds · {v.counts.decisions} decisions · {v.counts.skills} skills · {v.counts.industries} industries
                      </td>
                      <td className="px-3 py-3 text-xs text-neutral-600">
                        {formatDate(v.createdAt)}
                        {v.createdBy && <span className="block text-neutral-400">by {v.createdBy}</span>}
                        {v.publishedAt && <span className="block text-neutral-400">published {formatDate(v.publishedAt)}</span>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button variant="secondary" size="sm" onClick={() => preview(v)} loading={busy === `preview-${v.id}`}>
                            <Play size={13} className="mr-1" /> Preview
                          </Button>
                          {v.status !== 'published' && (
                            <Button variant="primary-filled" size="sm" onClick={() => publish(v)} loading={busy === `publish-${v.id}`}>
                              Publish
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => download(v, 'xlsx')} title="Download as Excel">
                            <Download size={13} className="mr-1" /> xlsx
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => download(v, 'json')} title="Download as JSON">
                            json
                          </Button>
                          {v.status === 'draft' && (
                            <Button variant="ghost" size="sm" onClick={() => remove(v)} loading={busy === `delete-${v.id}`} title="Delete draft">
                              <Trash2 size={13} className="text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-3 text-xs text-neutral-400">
            To change a published topic, download it as Excel, edit, and upload it again — it becomes the next version as a draft.
          </p>
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={() => pending?.run()}
        title={pending?.title || ''}
        message={pending?.message}
        confirmText={pending?.confirmText}
        variant={pending?.variant || 'info'}
      />
    </DashboardLayout>
  );
};

export default AdminMicroSimTopicPage;
